import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { getStatusColorClasses, getStatusLabel } from '../utils/nutritionalStatus';

/**
 * StatusDot Component - Shows nutritional status as a colored dot with tooltip
 * @param {string} status - 'normal', 'warning', or 'critical'
 * @param {string} color - 'green', 'yellow', or 'red'
 * @param {array} reasons - Array of reason strings to show in tooltip
 * @param {string} size - 'sm' (6px), 'md' (8px), or 'lg' (10px)
 */
export default function StatusDot({ status, color, reasons = [], size = 'md' }) {
  const colorClasses = getStatusColorClasses(color);
  const label = getStatusLabel(status);

  const sizeClasses = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5'
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center cursor-help">
            <div 
              className={`${sizeClasses[size]} rounded-full ${colorClasses.dot} ring-2 ring-white shadow-sm`}
              aria-label={`Nutritional status: ${label}`}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className={`max-w-xs ${colorClasses.bg} ${colorClasses.border} border`}
        >
          <div className="space-y-1">
            <p className={`font-semibold text-sm ${colorClasses.text}`}>
              Status: {label}
            </p>
            {reasons.length > 0 && (
              <ul className="text-xs space-y-0.5">
                {reasons.map((reason, index) => (
                  <li key={index} className="text-gray-700">
                    • {reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
